// src/modules/callSync/components/CallButton.jsx
// The Call button for the Lead UI.
//
// It no longer owns the call. The session lives in CallSessionContext, one per
// user, hydrated from the server — so this button shows the truth about the
// phone even after a browser reload, and every OTHER lead's button correctly
// refuses to dial while a call is already running.

import React from 'react';
import { Button, Tooltip } from 'antd';
import { PhoneOutlined, PhoneFilled } from '@ant-design/icons';
import { usePermissions } from '../../../hooks/usePermissions';
import { useCallSession } from '../context/CallSessionContext';

export default function CallButton({
  lead,
  size = 'middle',
  type = 'primary',
  block = false,
  onCallFinished,
}) {
  const { can } = usePermissions();
  const session = useCallSession();

  const leadId = lead?._id ? String(lead._id) : null;
  // Is the session's call about THIS lead? Matters for both the label and for
  // which row gets to show the live banner.
  const callLeadId = session.call?.lead
    ? String(session.call.lead._id || session.call.lead)
    : null;
  const isThisLead = !!leadId && callLeadId === leadId;
  const liveHere = isThisLead && session.isLive;
  const busyElsewhere = session.isLive && !isThisLead;

  // Lists refresh themselves when any call ends, without each row registering
  // its own callback on the session.
  const finishedAt = session.lastFinishedAt;
  const seenRef = React.useRef(finishedAt);
  React.useEffect(() => {
    if (finishedAt && finishedAt !== seenRef.current) {
      seenRef.current = finishedAt;
      onCallFinished?.(session.call);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishedAt]);

  // Only the button that was actually pressed spins. `starting` is global now,
  // and a table of 200 rows all spinning at once would be nonsense.
  const [pressed, setPressed] = React.useState(false);
  React.useEffect(() => { if (!session.starting) setPressed(false); }, [session.starting]);

  const allowed = can('callSync', 'initiate');
  const hasNumber = !!lead?.phone;
  // Until the server has said what is in flight, dialling could double-dial.
  const disabled = !allowed || !hasNumber || session.starting || session.isLive || !session.hydrated;

  const reason = !allowed
    ? 'You do not have permission to place calls'
    : !hasNumber
      ? 'This lead has no phone number'
      : !session.hydrated
        ? 'Checking whether a call is already running…'
        : liveHere
          ? 'This call is in progress on your phone'
          : busyElsewhere
            ? `Your phone is already on a call${session.call?.lead?.name ? ` with ${session.call.lead.name}` : ''}`
            : `Call ${lead?.name || 'this lead'} from your Android phone`;

  const label = liveHere ? 'On call' : busyElsewhere ? 'Busy' : 'Call';

  return (
    <>
      {/* The live status is NOT rendered here. It lives in GlobalCallBar, which
          is anchored to the viewport so it survives this row unmounting, the
          drawer closing and the page reloading. */}
      <Tooltip title={reason}>
        {/* A disabled antd Button swallows pointer events, so the tooltip needs
            its own wrapper to stay reachable. */}
        <span style={block ? { display: 'block' } : undefined}>
          <Button
            type={type}
            size={size}
            block={block}
            icon={liveHere ? <PhoneFilled /> : <PhoneOutlined />}
            loading={pressed && session.starting}
            disabled={disabled}
            danger={liveHere}
            onClick={() => { setPressed(true); session.start({ leadId: lead?._id, phoneNumber: lead?.phone, lead }); }}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
    </>
  );
}
